/** Policy autonome pour le cache React Query Prompts (testable sans navigateur).
 * Aucun JWT, email ou token de retour n'est stocké dans les query keys.
 */
export interface AuthPrincipal {
  isAuthenticated: boolean
  user: {
    id: string
    role: string
    status: string
    emailVerified: boolean
    subscriptionTier?: string | null
  } | null
}

export function authPrincipalKey(auth: AuthPrincipal): string {
  if (!auth.isAuthenticated || !auth.user) return 'anonymous'
  const { id, role, status, emailVerified, subscriptionTier } = auth.user
  return JSON.stringify([id, role, status, emailVerified, subscriptionTier ?? null])
}

/** Seules ces clés sont prouvées indépendantes du compte connecté.
 * La recherche Meili est publique, mais son fallback REST peut dépendre du JWT.
 * Toute clé inconnue (y compris Shared) est effacée par défaut.
 */
export function isPublicQuery(queryKey: readonly unknown[], data?: unknown): boolean {
  const [domain, resource] = queryKey
  if (domain === 'prompts') {
    if (resource === 'suggest') return true
    if (resource === 'search') {
      return !(
        typeof data === 'object' &&
        data !== null &&
        'fallback' in data &&
        data.fallback === 'rest'
      )
    }
  }
  if (domain === 'community' && (resource === 'profile' || resource === 'leaderboard')) {
    return true
  }
  if (domain === 'ratings' && (resource === 'summary' || resource === 'list')) return true
  if (domain === 'comments' && resource === 'list') return true
  if (domain === 'collections' && resource === 'shared') return true
  return false
}

export function authContextChanged(
  previous: AuthPrincipal & { accessToken: string | null },
  current: AuthPrincipal & { accessToken: string | null },
): boolean {
  // Même un échange de token avant /auth/me doit empêcher la réutilisation
  // d'une réponse privée d'une session précédente.
  return (
    previous.accessToken !== current.accessToken ||
    authPrincipalKey(previous) !== authPrincipalKey(current)
  )
}

/** Identifiant éphémère de rotation, sans conserver le bearer JWT dans les query keys.
 * Ce hash n'est pas une signature ni une décision d'autorisation.
 */
export function sessionTokenTag(token: string | null): string | null {
  if (!token) return null
  let value = 0xcbf29ce484222325n
  for (let index = 0; index < token.length; index += 1) {
    value ^= BigInt(token.charCodeAt(index))
    value = BigInt.asUintN(64, value * 0x100000001b3n)
  }
  return `${token.length}:${value.toString(36)}`
}
